import React, { useState, useEffect, useContext, useRef, useImperativeHandle } from "react";
import { IonIcon, IonModal } from "@ionic/react";
import { refreshOutline, colorWandOutline, sparklesOutline, cutOutline, closeOutline, checkmarkOutline } from "ionicons/icons";
import { Input, Progress } from "antd";
import { useAuth } from "../../../contexts/AuthContext";
import PaywallModal from "../../PaywallModal/PaywallModal";
import IOSPaywall from "../../PaywallModal/IOSPaywall";
import { isNativeiOS } from "../../../lib/utils/platform";
import AppContext from "../../../contexts/AppContext";
import axios from "axios";
import { processAndAnalyzeImage } from "./AutoUploadImage";
import { removeBackground } from "@imgly/background-removal";
import { blobToBase64 } from "../helpers/helpers";
import { processImage } from "./processImage";
import { requestRetrace } from "../../../lib/firebase/retraceImage";
import { requestModify } from "../../../lib/firebase/modifyImage";
import { creditTopUpHandles } from "../../../lib/creditTopUp";
import { isEmbroideryTrace } from "../../../config/appVariant";

const { TextArea } = Input;


// Loading steps for Retrace
const retraceLoadingSteps = [
  { text: "Analyzing image composition...", subtext: "AI is examining colors, shapes, and gradients", progress: 10 },
  { text: "Detecting key features...", subtext: "Identifying edges, boundaries, and important details", progress: 22 },
  { text: "Flattening gradients to solid colors...", subtext: "Converting smooth transitions for clean vector output", progress: 35 },
  { text: "Reconstructing with AI vision...", subtext: "Neural network is redrawing your image in flat style", progress: 50 },
  { text: "Sharpening lines and edges...", subtext: "Enhancing crispness for better SVG conversion", progress: 65 },
  { text: "Optimizing color palette...", subtext: "Ensuring accurate, distinct colors for tracing", progress: 78 },
  { text: "Applying final enhancements...", subtext: "AI is polishing the output for best results", progress: 90 },
  { text: "Almost ready...", subtext: "Preparing your optimized image", progress: 98 },
];

// Loading steps for Modify
const modifyLoadingSteps = [
  { text: "Analyzing your image...", subtext: "AI is understanding the image content and structure", progress: 15 },
  { text: "Understanding modification request...", subtext: "Interpreting your instructions for the AI", progress: 30 },
  { text: "Applying AI modifications...", subtext: "Neural network is making the requested changes", progress: 50 },
  { text: "Enhancing details...", subtext: "Refining and polishing the modified areas", progress: 70 },
  { text: "Finalizing changes...", subtext: "Ensuring consistency and quality", progress: 90 },
  { text: "Almost ready...", subtext: "Preparing your modified image", progress: 98 },
];

const AIToolkitBar = React.forwardRef(({
  onRetracedArtwork,
  file,
  setFile,
  colors,
  setColors,
  crop,
  setCrop,
  setImageDimensions,
  despeckle,
  setDespeckle,
  setOriginalDimensions,
  setLargeImageWarning,
  autoTriggerRetrace = false,
  setWasUpscaled,
  guestMode = false,
  pendingFileId = null,
  // Props for direct SVG preview update in guest mode
  setSvgPreview,
  setSvg,
  simplifyCurves,
  smoothness,
  ai,
  originalDimensions,
  useSuperpixels,
  onPaywallTrigger,
  setGuestProcessingFile, // For guest mode: sets the retraced file for SVG processing
}, ref) => {
  const { image64, setImage64 } = useContext(AppContext);
  const { user, userIsPremium, userHasAIAccess, isLoading: authLoading } = useAuth();
  const [toastMessage, setToastMessage] = useState(null);
  const toastTimerRef = useRef(null);

  // State management
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [iosPaywallAction, setIosPaywallAction] = useState(null);
  const [isModifyModalVisible, setIsModifyModalVisible] = useState(false);
  const [modificationRequest, setModificationRequest] = useState("");
  const [processingImage, setProcessingImage] = useState(false);
  const [processingType, setProcessingType] = useState(""); // "retrace", "modify", or "remove-bg"
  const [loadingMessage, setLoadingMessage] = useState("");
  const [loadingSubtext, setLoadingSubtext] = useState("");
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [messageKey, setMessageKey] = useState(0);
  const [isRetraceHovered, setIsRetraceHovered] = useState(false);
  const [isModifyHovered, setIsModifyHovered] = useState(false);
  const [isRemoveBgHovered, setIsRemoveBgHovered] = useState(false);
  
  // Ref for AbortController to cancel requests
  const abortControllerRef = useRef(null);

  const retraceRef = useRef(null);

  useImperativeHandle(ref, () => ({
    triggerRetrace: () => retraceRef.current && retraceRef.current(),
  }));

  const openNotification = (message, type) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToastMessage({ message, type });
    toastTimerRef.current = setTimeout(() => setToastMessage(null), 2500);
  };

  // Cancel the current processing operation
  const handleCancelProcessing = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    // Clear the wasUpscaled flag so it doesn't re-trigger
    if (setWasUpscaled) {
      setWasUpscaled(false);
    }
    
    setProcessingImage(false);
    setProcessingType("");
    openNotification("Processing cancelled", "info");
  };

  // Auto-trigger retrace for low-res images
  // For users without AI access (lifetime-pro), show the paywall instead
  useEffect(() => {
    if (authLoading || !autoTriggerRetrace || !image64 || !file || processingImage) return;

    if (guestMode) {
      if (pendingFileId) {
        const timer = setTimeout(() => handleRetraceInternal(true), 500);
        return () => clearTimeout(timer);
      }
      return;
    }

    if (!user) return;

    if (userHasAIAccess) {
      const timer = setTimeout(() => handleRetraceInternal(true), 500);
      return () => clearTimeout(timer);
    }

    // User is logged in but doesn't have AI access — show paywall upsell
    const timer = setTimeout(() => {
      if (isNativeiOS()) {
        setIosPaywallAction("auto-retrace");
      } else if (onPaywallTrigger) {
        onPaywallTrigger("ai-toolkit");
      } else {
        setShowPaywallModal(true);
      }
    }, 800);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, autoTriggerRetrace, image64, file, user, userHasAIAccess, guestMode, pendingFileId]);

  useEffect(() => {
    if (!authLoading && userHasAIAccess) setShowPaywallModal(false);
  }, [authLoading, userHasAIAccess]);

  // Update loading progress and messages smoothly
  useEffect(() => {
    if (!processingImage) {
      setLoadingProgress(0);
      setLoadingMessage("");
      setLoadingSubtext("");
      return;
    }
    
    const loadingSteps = processingType === "modify" ? modifyLoadingSteps : retraceLoadingSteps;
    const totalDuration = processingType === "modify" ? 20000 : 15000; // 20s for modify, 15s for retrace (Nano Banana is fast)
    
    const startTime = Date.now();
    
    setLoadingMessage(loadingSteps[0].text);
    setLoadingSubtext(loadingSteps[0].subtext || "");
    setLoadingProgress(0);
    setMessageKey(prev => prev + 1);
    
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progressPercentage = Math.min((elapsed / totalDuration) * 98, 98);
      
      setLoadingProgress(progressPercentage);
      
      const stepIndex = Math.floor((progressPercentage / 98) * loadingSteps.length);
      const currentStep = loadingSteps[Math.min(stepIndex, loadingSteps.length - 1)];
      setLoadingMessage(currentStep.text);
      setLoadingSubtext(currentStep.subtext || "");
      setMessageKey(prev => prev + 1);
    }, 500);
    
    return () => {
      clearInterval(progressInterval);
      setLoadingMessage("");
      setLoadingSubtext("");
      setLoadingProgress(0);
      setMessageKey(0);
    };
  }, [processingImage, processingType]);

  const handleRetraceInternal = async (isAutoTriggered = false) => {
    if (!image64 || !file) {
      if (!isAutoTriggered) {
        openNotification("No image available to retrace", "error");
      }
      return;
    }

    // Guest mode requires pendingFileId
    if (guestMode && !pendingFileId) {
      if (!isAutoTriggered) {
        openNotification("Unable to retrace - please try uploading again", "error");
      }
      return;
    }

    // Authenticated mode requires user
    if ((isEmbroideryTrace || !guestMode) && !user) {
      if (!isAutoTriggered) {
        openNotification("Please sign in to use retrace", "error");
      }
      return;
    }

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    setProcessingType("retrace");
    setProcessingImage(true);

    try {
      // Extract base64 string without data:image prefix
      const base64String = image64.split(',')[1] || image64;
      
      const resp = await requestRetrace(base64String, user, {
        guestMode, pendingFileId, signal: abortControllerRef.current.signal,
      });

      const retracedImageData = "data:image/png;base64," + resp.data.base64;
      
      // In guest mode: Set the retraced file for processing
      // The debounced effect will use this file for SVG generation
      // while the original file stays displayed in the canvas
      if (guestMode && setGuestProcessingFile) {
        console.log("🎨 Guest mode: Setting retraced file for processing...");
        console.log("🎨 Retraced image data length:", retracedImageData?.length);
        
        // Analyze the retraced image to get new colors AND dimensions
        console.log("🎨 Rescanning colors from retraced image...");
        const analysisResult = await processAndAnalyzeImage(retracedImageData);
        onRetracedArtwork?.(retracedImageData, analysisResult.imageDimensions);
        console.log("🎨 Analysis result - colors count:", analysisResult?.colors?.length);
        console.log("🎨 Analysis result - NEW colors detected:", analysisResult?.colors);
        console.log("🎨 Analysis result - dimensions:", analysisResult?.originalDimensions);
        console.log("🎨 Analysis result - crop:", analysisResult?.crop);
        
        // IMPORTANT: Update crop and dimensions BEFORE setting guestProcessingFile
        // This ensures the SVG generation uses the correct dimensions for the retraced image
        if (setOriginalDimensions && analysisResult.originalDimensions) {
          setOriginalDimensions(analysisResult.originalDimensions);
          console.log("🎨 Updated originalDimensions to:", analysisResult.originalDimensions);
        }
        if (setCrop && analysisResult.crop) {
          setCrop(analysisResult.crop);
          console.log("🎨 Updated crop to:", analysisResult.crop);
        }
        
        if (setImageDimensions && analysisResult.imageDimensions) {
          setImageDimensions(analysisResult.imageDimensions);
        }

        // Set guestProcessingFile BEFORE colors
        // This ensures when colors change triggers debounce, the retraced file is ready
        setGuestProcessingFile(retracedImageData);
        console.log("🎨 Guest processing file set");
        
        // Update colors from the retraced (AI enhanced) image
        // This replaces the old colors with freshly detected colors from the enhanced image
        if (analysisResult.colors && analysisResult.colors.length > 0) {
          console.log("🎨 Setting NEW colors from retraced image:", analysisResult.colors);
          setColors(analysisResult.colors);
          console.log("🎨 Colors updated! SVG will regenerate with new colors from enhanced image");
        } else {
          console.warn("🎨 No colors detected from retraced image, keeping existing colors");
        }
        
        // Clear wasUpscaled LAST so all state updates are batched in one render
        if (setWasUpscaled) {
          setWasUpscaled(false);
        }
        
        openNotification(
          isAutoTriggered 
            ? "✨ AI enhanced your image! Sign up to download." 
            : "Image retraced! Sign up to download.", 
          "success"
        );
        return retracedImageData;
      }
      
      // For authenticated users, update everything including original image
      const result = await processAndAnalyzeImage(retracedImageData);
      onRetracedArtwork?.(retracedImageData, result.imageDimensions);
      setImage64(retracedImageData);
      setFile(result.file);
      if (setOriginalDimensions) {
        setOriginalDimensions(result.originalDimensions);
      }
      if (setLargeImageWarning) {
        setLargeImageWarning(result.largeImageWarning);
      }
      setCrop(result.crop);
      if (setImageDimensions) {
        setImageDimensions(result.imageDimensions);
      }
      if (setDespeckle) {
        setDespeckle(result.despeckle);
      }
      setColors(result.colors);
      
      // Clear wasUpscaled LAST so all state updates are batched in one render
      if (setWasUpscaled) {
        setWasUpscaled(false);
      }
      
      openNotification(
        isAutoTriggered 
          ? "Low-res image automatically enhanced!" 
          : "Image retraced successfully!", 
        "success"
      );

      return result.file;
      
    } catch (error) {
      // Don't show error if request was cancelled by user
      if (axios.isCancel(error) || error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        console.log('Retrace request cancelled by user');
        return;
      }
      
      console.error('Retrace error:', error);
      
      // Clear wasUpscaled flag even on error to prevent retry loops
      if (setWasUpscaled) {
        setWasUpscaled(false);
      }

      // The credit shop already explains exhaustion; a red toast on top of it
      // reads as a failure rather than something the user can fix.
      if (creditTopUpHandles(error)) {
        return;
      }
      
      // Guest-specific errors
      if (guestMode && error.response?.status === 429) {
        openNotification("Too many requests. Please wait a moment and try again.", "info");
        return;
      }
      
      if (error.response?.status === 401) {
        openNotification("Authentication failed. Please log in again.", "error");
      } else if (error.response?.status === 403) {
        openNotification(isEmbroideryTrace ? error.response?.data?.message || "An active EmbroideryTrace account is required." : "This feature requires a premium subscription.", "error");
      } else if (error.code === 'ECONNABORTED') {
        openNotification(isEmbroideryTrace ? "The connection timed out. Processing may have continued; check your AI usage before starting another Retrace." : "Request timed out. Please try again.", "error");
      } else {
        openNotification(
          error.response?.data?.message || "Failed to retrace image. Please try again.",
          "error"
        );
      }
    } finally {
      abortControllerRef.current = null;
      setProcessingImage(false);
      setProcessingType("");
    }
  };

  const handleRetrace = () => handleRetraceInternal(false);
  retraceRef.current = handleRetrace;

  const hasProAccess = user && userHasAIAccess;

  // Wrapper to check Pro access before executing action (featureName used for iOS paywall)
  const requireProAccess = (action, featureName = "ai-toolkit") => {
    if (authLoading) return;
    if (!hasProAccess) {
      if (isNativeiOS()) {
        setIosPaywallAction(featureName);
      } else if (onPaywallTrigger) {
        onPaywallTrigger('ai-toolkit');
      } else {
        setShowPaywallModal(true);
      }
      return;
    }
    action();
  };

  const handleModify = async () => {
    if (!modificationRequest || modificationRequest.trim() === "") {
      openNotification("Please enter a modification request", "error");
      return;
    }

    if (!image64 || !file) {
      openNotification("No image available to modify", "error");
      return;
    }

    setIsModifyModalVisible(false);
    setProcessingType("modify");
    setProcessingImage(true);

    try {
      const resp = await requestModify(image64, modificationRequest, user);

      const modifiedImageData = "data:image/png;base64," + resp.data.base64;
      
      // Update the image in context
      setImage64(modifiedImageData);
      
      // Re-analyze the image to detect new colors and dimensions
      const result = await processAndAnalyzeImage(modifiedImageData);
      setFile(result.file);
      if (setOriginalDimensions) {
        setOriginalDimensions(result.originalDimensions);
      }
      if (setLargeImageWarning) {
        setLargeImageWarning(result.largeImageWarning);
      }
      setCrop(result.crop);
      if (setImageDimensions) {
        setImageDimensions(result.imageDimensions);
      }
      if (setDespeckle) {
        setDespeckle(result.despeckle);
      }
      setColors(result.colors);
      
      openNotification("Image modified successfully!", "success");
      setModificationRequest(""); // Reset the request
      
    } catch (error) {
      console.error('Modify error:', error);

      if (creditTopUpHandles(error)) {
        return;
      }

      if (error.response?.status === 401) {
        openNotification("Authentication failed. Please log in again.", "error");
      } else if (error.response?.status === 403) {
        openNotification(isEmbroideryTrace ? error.response?.data?.message || "An active EmbroideryTrace account is required." : "This feature requires a premium subscription.", "error");
      } else if (error.code === 'ECONNABORTED') {
        openNotification(isEmbroideryTrace ? "The connection timed out. Check your AI usage before starting another Modify request." : "Request timed out. Please try again.", "error");
      } else {
        openNotification(
          error.response?.data?.message || "Failed to modify image. Please try again.",
          "error"
        );
      }
    } finally {
      setProcessingImage(false);
      setProcessingType("");
    }
  };

  const handleRemoveBackground = async () => {
    if (!image64 || !file) {
      openNotification("No image available to remove background", "error");
      return;
    }

    setProcessingType("remove-bg");
    setProcessingImage(true);

    try {
      const blob = await removeBackground(file);
      const base64String = await blobToBase64(blob);
      
      // Update the image in context
      setImage64(base64String);
      
      // Re-analyze the image to detect new colors and dimensions
      const result = await processAndAnalyzeImage(base64String);
      setFile(result.file);
      if (setOriginalDimensions) {
        setOriginalDimensions(result.originalDimensions);
      }
      if (setLargeImageWarning) {
        setLargeImageWarning(result.largeImageWarning);
      }
      setCrop(result.crop);
      if (setImageDimensions) {
        setImageDimensions(result.imageDimensions);
      }
      if (setDespeckle) {
        setDespeckle(result.despeckle);
      }
      setColors(result.colors);
      
      openNotification("Background removed successfully!", "success");
      
    } catch (error) {
      console.error('Remove background error:', error);
      openNotification("Failed to remove background. Please try again.", "error");
    } finally {
      setProcessingImage(false);
      setProcessingType("");
    }
  };

  return (
    <>
      {toastMessage && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
          zIndex: 99999,
        }}>
          <div style={{
            background: toastMessage.type === "error" ? "#ff4d4f"
              : toastMessage.type === "success" ? "#52c41a"
              : "#1677ff",
            color: "#fff",
            padding: "12px 24px",
            borderRadius: "12px",
            fontSize: "15px",
            fontWeight: 500,
            maxWidth: "85%",
            textAlign: "center",
            boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
            animation: "fadeInOut 2.5s ease-in-out",
          }}>
            {toastMessage.message}
          </div>
          <style>{`
            @keyframes fadeInOut {
              0% { opacity: 0; transform: scale(0.9); }
              10% { opacity: 1; transform: scale(1); }
              80% { opacity: 1; transform: scale(1); }
              100% { opacity: 0; transform: scale(0.9); }
            }
          `}</style>
        </div>
      )}
      
      {/* Custom Loading Modal with Progress */}
      <IonModal
        isOpen={processingImage}
        backdropDismiss={false}
        style={{
          "--width": "500px",
          "--max-width": "90%",
          "--height": "auto",
          "--border-radius": "20px",
        }}
      >
        <div
          style={{
            background: "white",
            padding: "40px 30px",
            borderRadius: "20px",
            color: "#1a202c",
            fontFamily: "Clash Grotesk",
            textAlign: "center",
          }}
        >
          <h2 style={{
            fontSize: "24px",
            fontWeight: "600",
            marginBottom: "20px",
            margin: "0 0 20px 0",
            color: "#1a202c",
          }}>
            {processingType === "retrace" 
              ? "Tracing Your Image..." 
              : processingType === "modify" 
              ? "Modifying Your Image..."
              : "Removing Background..."}
          </h2>
          
          <div style={{
            marginBottom: "24px",
          }}>
            {processingType !== "remove-bg" ? (
              <>
                <p
                  key={messageKey}
                  style={{
                    fontSize: "15px",
                    marginBottom: "8px",
                    color: "#4a5568",
                    minHeight: "22px",
                    fontWeight: "500",
                  }}
                >
                  {loadingMessage}
                </p>
                {loadingSubtext && (
                  <p
                    style={{
                      fontSize: "13px",
                      marginBottom: "16px",
                      color: "#718096",
                      minHeight: "18px",
                      fontWeight: "400",
                      fontStyle: "italic",
                    }}
                  >
                    {loadingSubtext}
                  </p>
                )}
                
                <Progress
                  percent={Math.round(loadingProgress)}
                  strokeColor={{
                    '0%': '#667eea',
                    '100%': '#764ba2',
                  }}
                  trailColor="#e2e8f0"
                  strokeWidth={12}
                  showInfo={false}
                  style={{
                    marginBottom: "12px",
                  }}
                />
                
                <div style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#667eea",
                }}>
                  {Math.round(loadingProgress)}%
                </div>
              </>
            ) : (
              <p style={{
                fontSize: "15px",
                color: "#4a5568",
                fontWeight: "500",
              }}>
                Processing your image...
              </p>
            )}
          </div>
          
          <div style={{
            fontSize: "13px",
            color: "#718096",
            marginTop: "20px",
          }}>
            {processingType === "remove-bg" 
              ? "This usually takes just a few seconds..."
              : "This usually takes 15-30 seconds..."}
          </div>
          
          <button
            onClick={handleCancelProcessing}
            style={{
              marginTop: "24px",
              marginLeft: "auto",
              marginRight: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
              padding: "10px 24px",
              backgroundColor: "#f1f5f9",
              color: "#64748b",
              border: "1px solid #e2e8f0",
              borderRadius: "10px",
              fontSize: "14px",
              fontWeight: 600,
              fontFamily: "Clash Grotesk",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#e2e8f0";
              e.target.style.color = "#475569";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "#f1f5f9";
              e.target.style.color = "#64748b";
            }}
          >
            <IonIcon icon={closeOutline} style={{ fontSize: "18px" }} />
            Cancel
          </button>
        </div>
      </IonModal>
      
      {/* Modify Input Box - Appears below AI Toolkit */}
      {isModifyModalVisible && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 12px)",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1001,
            background: "white",
            borderRadius: "16px",
            padding: "16px",
            boxShadow: "0 12px 48px rgba(102, 126, 234, 0.3)",
            width: "500px",
            maxWidth: window.innerWidth <= 768 ? "calc(100vw - 48px)" : "90vw",
            border: "2px solid rgba(102, 126, 234, 0.2)",
          }}
        >
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "12px",
            color: "#6B21A8",
            fontFamily: "Clash Grotesk",
            fontWeight: 600,
            fontSize: "15px",
          }}>
            <IonIcon icon={colorWandOutline} style={{ fontSize: "18px" }} />
            <span>Modify Image</span>
          </div>
          
          <TextArea
            value={modificationRequest}
            onChange={(e) => setModificationRequest(e.target.value)}
            placeholder="Describe your changes... (e.g., change text to 'Dog', make background blue, combine elements from multiple images)"
            autoSize={{ minRows: 2, maxRows: 3 }}
            maxLength={300}
            showCount
            autoFocus
            style={{
              fontFamily: "Clash Grotesk",
              fontSize: "13px",
              borderRadius: "8px",
              border: "2px solid #e2e8f0",
              marginBottom: "12px",
            }}
          />
          
          <div style={{
            display: "flex",
            gap: "8px",
            justifyContent: "flex-end",
          }}>
            <button
              onClick={() => {
                setIsModifyModalVisible(false);
                setModificationRequest("");
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 16px",
                backgroundColor: "#f1f5f9",
                color: "#64748b",
                border: "none",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: 600,
                fontFamily: "Clash Grotesk",
                cursor: "pointer",
              }}
            >
              <IonIcon icon={closeOutline} style={{ fontSize: "16px" }} />
              Cancel
            </button>
            <button
              onClick={handleModify}
              disabled={!modificationRequest.trim()}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 16px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: 600,
                fontFamily: "Clash Grotesk",
                cursor: !modificationRequest.trim() ? "not-allowed" : "pointer",
                opacity: !modificationRequest.trim() ? 0.5 : 1,
              }}
            >
              <IonIcon icon={checkmarkOutline} style={{ fontSize: "16px" }} />
              Modify
            </button>
          </div>
        </div>
      )}
      
      <div
        data-tutorial="ai-toolkit-bar"
        style={{
          position: "relative",
          zIndex: 1,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          borderRadius: window.innerWidth <= 768 ? "18px" : "14px",
          padding: window.innerWidth <= 768 ? "4px 8px" : "5px 8px 5px 14px",
          boxShadow: window.innerWidth <= 768 ? "none" : "0 4px 16px rgba(102, 126, 234, 0.3)",
          display: "flex",
          alignItems: "center",
          gap: window.innerWidth <= 768 ? "3px" : "6px",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.18)",
          flexShrink: 0,
        }}
      >
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: window.innerWidth <= 768 ? "4px" : "6px",
          color: "rgba(255, 255, 255, 0.9)",
          fontSize: window.innerWidth <= 768 ? "11px" : "14px",
          fontWeight: 600,
          fontFamily: "Clash Grotesk",
          marginRight: window.innerWidth <= 768 ? "2px" : "4px",
          letterSpacing: "0.02em",
        }}>
          <IonIcon icon={sparklesOutline} style={{ fontSize: window.innerWidth <= 768 ? "13px" : "17px" }} />
          <span>AI</span>
        </div>

        <button
          data-tutorial="ai-retrace-button"
          onClick={() => requireProAccess(handleRetrace, "retrace")}
          disabled={!image64 || processingImage}
          onMouseEnter={() => setIsRetraceHovered(true)}
          onMouseLeave={() => setIsRetraceHovered(false)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: window.innerWidth <= 768 ? "4px" : "6px",
            padding: window.innerWidth <= 768 ? "6px 10px" : "7px 14px",
            backgroundColor: isRetraceHovered ? "rgba(255, 255, 255, 0.25)" : "rgba(255, 255, 255, 0.12)",
            color: "white",
            border: "none",
            borderRadius: window.innerWidth <= 768 ? "18px" : "9px",
            fontSize: window.innerWidth <= 768 ? "11px" : "13px",
            fontWeight: 500,
            fontFamily: "Clash Grotesk",
            cursor: !image64 || processingImage ? "not-allowed" : "pointer",
            opacity: !image64 || processingImage ? 0.5 : 1,
            transition: "all 0.15s ease",
          }}
        >
          <IonIcon icon={refreshOutline} style={{ fontSize: window.innerWidth <= 768 ? "13px" : "15px" }} />
          <span>Retrace</span>
        </button>

        <button
          data-tutorial="ai-modify-button"
          onClick={() => requireProAccess(() => setIsModifyModalVisible(true), "modify")}
          disabled={!image64 || processingImage}
          onMouseEnter={() => setIsModifyHovered(true)}
          onMouseLeave={() => setIsModifyHovered(false)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: window.innerWidth <= 768 ? "4px" : "6px",
            padding: window.innerWidth <= 768 ? "6px 10px" : "7px 14px",
            backgroundColor: isModifyHovered ? "rgba(255, 255, 255, 0.25)" : "rgba(255, 255, 255, 0.12)",
            color: "white",
            border: "none",
            borderRadius: window.innerWidth <= 768 ? "18px" : "9px",
            fontSize: window.innerWidth <= 768 ? "11px" : "13px",
            fontWeight: 500,
            fontFamily: "Clash Grotesk",
            cursor: !image64 || processingImage ? "not-allowed" : "pointer",
            opacity: !image64 || processingImage ? 0.5 : 1,
            transition: "all 0.15s ease",
          }}
        >
          <IonIcon icon={colorWandOutline} style={{ fontSize: window.innerWidth <= 768 ? "13px" : "15px" }} />
          <span>Modify</span>
        </button>

        {!isEmbroideryTrace && <button
          data-tutorial="ai-remove-bg-button"
          onClick={() => requireProAccess(handleRemoveBackground, "ai-toolkit")}
          disabled={!image64 || processingImage}
          onMouseEnter={() => setIsRemoveBgHovered(true)}
          onMouseLeave={() => setIsRemoveBgHovered(false)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: window.innerWidth <= 768 ? "4px" : "6px",
            padding: window.innerWidth <= 768 ? "6px 10px" : "7px 14px",
            backgroundColor: isRemoveBgHovered ? "rgba(255, 255, 255, 0.25)" : "rgba(255, 255, 255, 0.12)",
            color: "white",
            border: "none",
            borderRadius: window.innerWidth <= 768 ? "18px" : "9px",
            fontSize: window.innerWidth <= 768 ? "11px" : "13px",
            fontWeight: 500,
            fontFamily: "Clash Grotesk",
            cursor: !image64 || processingImage ? "not-allowed" : "pointer",
            opacity: !image64 || processingImage ? 0.5 : 1,
            transition: "all 0.15s ease",
          }}
        >
          <IonIcon icon={cutOutline} style={{ fontSize: window.innerWidth <= 768 ? "13px" : "15px" }} />
          <span style={{ whiteSpace: "nowrap" }}>{window.innerWidth <= 768 ? "BG" : "Remove BG"}</span>
        </button>}
      </div>


      {/* Paywall Modal for Pro features (web only) */}
      {!onPaywallTrigger && !isNativeiOS() && (
        <PaywallModal
          isVisible={showPaywallModal}
          onClose={() => setShowPaywallModal(false)}
          isAuthenticated={!!user}
          isPremium={userHasAIAccess}
          action="ai-toolkit"
        />
      )}

      {/* iOS paywall — shown when non-premium user tries to use AI features */}
      {iosPaywallAction && (
        <IOSPaywall
          standalone
          action={iosPaywallAction}
          onClose={() => setIosPaywallAction(null)}
          onSuccess={() => setIosPaywallAction(null)}
        />
      )}
    </>
  );
});

export default AIToolkitBar;
